import axios from 'axios';
import { externalApiClient } from '@/shared/lib/apiClient';
import { serializeApiPayload } from '@/shared/lib/typeUtil';
import type { PlannerView } from './planner.types';
import type { PlannerCounselSlotResponse } from './planner-counsel-slot.api';
import type {
  VisitCounselView,
  VisitCounselSearchQuery,
  VisitCounselSearchView,
  VisitCounselEventView,
  StartVisitCounselDepartureAddress,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import type { StartHouseSketchSessionRequest } from '@/entities/visit-counsel/model/house-sketch.types';
import type {
  StartRoomObservingSessionRequest,
  RecordRoomObservingVideoRequest,
  RecordRoomObservingMovingItemsRequest,
  RecordAdditionalSpaceObservingMovingItemsRequest,
  RecordMovingItemServiceSetsRequest,
  StartAdditionalSpaceObservingSessionRequest,
  RecordAdditionalSpaceObservingVideoRequest,
} from '@/entities/visit-counsel/model/room-observing.types';
import type {
  RecordDepartureSiteRequest,
  RecordArrivalSiteRequest,
  RecordMovingDateRequest,
  RecordPackingRequest,
  RecordCustomerRequestsRequest,
} from '@/entities/visit-counsel/model/intake.types';
import type {
  MovingTypeSpecificationResponse,
  RecordMovingTypeIntakeRequest,
} from '@/entities/visit-counsel/model/moving-type-intake.types';

/**
 * 서버 ErrorDetail 의 code/httpStatus 를 보존한 에러.
 * 호출부(예: 영상 업로드 파이프라인)가 재시도 가능 여부를 판별하는 데 사용한다.
 */
export interface PlannerApiError extends Error {
  code?: string;
  httpStatus?: number;
}

function toPlannerApiError(
  error: unknown,
  fallbackMessage: string,
  unknownErrorMessage = '알 수 없는 오류가 발생했습니다.'
): PlannerApiError {
  if (axios.isAxiosError(error)) {
    const msg =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      error.message ||
      fallbackMessage;
    const apiError: PlannerApiError = new Error(msg);
    apiError.code = error.response?.data?.code;
    apiError.httpStatus = error.response?.status;
    return apiError;
  }
  return new Error(unknownErrorMessage);
}

/**
 * PLANNER 전용 API (GET /api/v1/planners/me, /api/v1/planners/me/visit-counsels/slots)
 * JWT 인증, 현재 로그인한 플래너 ID로 자동 제한
 */
export const myPlannerApi = {
  /**
   * 현재 로그인한 플래너의 프로필 정보
   */
  async getMyPlanner(): Promise<PlannerView> {
    try {
      const response = await externalApiClient.get<PlannerView>('/api/v1/planners/me', {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '플래너 정보 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 현재 플래너의 방문상담 슬롯 목록 (특정 날짜)
   */
  async getMySlotsByDate(date: string): Promise<PlannerCounselSlotResponse[]> {
    try {
      const response = await externalApiClient.get<PlannerCounselSlotResponse[]>(
        '/api/v1/planners/me/visit-counsels/slots',
        { headers: { 'Content-Type': 'application/json' }, params: { date } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '슬롯 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 현재 플래너의 방문상담 슬롯 목록 (날짜 범위)
   */
  async getMySlotsByDateRange(
    startDate: string,
    endDate: string
  ): Promise<PlannerCounselSlotResponse[]> {
    try {
      const response = await externalApiClient.get<PlannerCounselSlotResponse[]>(
        '/api/v1/planners/me/visit-counsels/slots',
        {
          headers: { 'Content-Type': 'application/json' },
          params: { startDate, endDate },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '슬롯 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },
};

/**
 * PLANNER 전용 visit-counsels API (도메인 컨트롤러 /api/v1/visit-counsels/*)
 * PLANNER 로그인 시 서버가 자동으로 본인 배정 상담만 조회
 */
export const myVisitCounselApi = {
  /**
   * 본인에게 배정된 방문상담 검색 (PLANNER 전용, counselorId 자동 필터)
   * POST /api/v1/visit-counsels/search/my
   */
  async searchMyVisitCounsels(query: VisitCounselSearchQuery): Promise<VisitCounselSearchView> {
    try {
      const response = await externalApiClient.post<VisitCounselSearchView>(
        '/api/v1/visit-counsels/search/my',
        serializeApiPayload(query),
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '방문 상담 검색에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 본인에게 배정된 방문상담 상세 (PLANNER 전용)
   * GET /api/v1/visit-counsels/my/{visitCounselId}
   */
  async getMyVisitCounselById(visitCounselId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.get<VisitCounselView>(
        `/api/v1/visit-counsels/my/${visitCounselId}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '방문 상담 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 방문 상담 시작 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/start
   * 담당 플래너만 호출 가능. 출발지 주소를 구조화하여 기록합니다.
   * floor 또는 unit 둘 중 정확히 하나 필수.
   *
   * 409: 이미 시작된 상태 — 에러 대신 현재 counsel 상태를 조회해 반환한다.
   */
  async startVisitCounsel(
    visitCounselId: string,
    request: { departureAddress: StartVisitCounselDepartureAddress }
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/start`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          const current = await externalApiClient.get<VisitCounselView>(
            `/api/v1/visit-counsels/my/${visitCounselId}`,
            { headers: { 'Content-Type': 'application/json' } }
          );
          return current.data;
        }
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '상담 시작에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 영상 촬영하기 - 스케치 세션 시작 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/sketching-session/start
   *
   * 409: 세션이 이미 존재하는 경우 — 에러 대신 현재 counsel 상태를 조회해 반환한다.
   * (재시도 시나리오: 첫 POST는 성공했지만 화면 전환이 안 된 채로 사용자가 다시 누른 경우)
   */
  async startHouseSketchSession(
    visitCounselId: string,
    request: StartHouseSketchSessionRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/sketching-session/start`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          // 스케칭 세션이 이미 존재 → 현재 counsel 상태를 조회해 정상 흐름으로 진행
          const current = await externalApiClient.get<VisitCounselView>(
            `/api/v1/visit-counsels/my/${visitCounselId}`,
            { headers: { 'Content-Type': 'application/json' } }
          );
          return current.data;
        }
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '영상 촬영 시작에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 스케치 세션에 방 추가 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/sketching-session/rooms
   * sketching STARTED 상태에서만 호출 가능.
   * index는 서버에서 자동 할당.
   */
  async addSketchingRoom(
    visitCounselId: string,
    request: { type: string; namePrefix: string }
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/sketching-session/rooms`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '방 추가에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('방 추가에 실패했습니다.');
    }
  },

  /**
   * 집 스케치 세션 완료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/sketching-session/complete
   * STARTED 상태에서만 호출 가능. 영상을 포함해 세션을 COMPLETED로 전환.
   */
  async completeSketchingSession(
    visitCounselId: string,
    request: { videoToken: string }
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/sketching-session/complete`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '집 스케치 세션 완료에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('집 스케치 세션 완료에 실패했습니다.');
    }
  },

  /**
   * 집 스케치 영상 수정 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/sketching-session/video
   * COMPLETED 상태에서만 호출 가능. 기존 영상 수정(재촬영/재업로드) 시 사용.
   */
  async recordSketchingSessionVideo(
    visitCounselId: string,
    request: { videoToken: string }
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/sketching-session/video`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '집 스케치 영상 등록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('집 스케치 영상 등록에 실패했습니다.');
    }
  },

  /**
   * 스케치 세션에서 방 제거 (PLANNER 전용)
   * DELETE /api/v1/visit-counsels/{visitCounselId}/sketching-session/rooms/{roomId}
   * 해당 방 옵저빙이 REQUESTED일 때만 호출 가능.
   */
  async removeSketchingRoom(visitCounselId: string, roomId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.delete<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/sketching-session/rooms/${roomId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '방 제거에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('방 제거에 실패했습니다.');
    }
  },

  /**
   * 본인에게 배정된 방문상담의 이벤트 목록 (PLANNER 전용)
   * GET /api/v1/visit-counsels/my/{visitCounselId}/events
   */
  async getMyVisitCounselEvents(visitCounselId: string): Promise<VisitCounselEventView> {
    try {
      const response = await externalApiClient.get<VisitCounselEventView>(
        `/api/v1/visit-counsels/my/${visitCounselId}/events`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '이벤트 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 방 옵저빙 세션 시작 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/room-observing-sessions/{sessionId}/start
   * REQUESTED 상태에서만 호출 가능. sessionId는 roomObserving 세션 ID.
   */
  async startRoomObservingSession(
    visitCounselId: string,
    sessionId: string,
    request: StartRoomObservingSessionRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/room-observing-sessions/${sessionId}/start`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '방 옵저빙 세션 시작에 실패했습니다.');
    }
  },

  /**
   * 방 옵저빙 세션 영상 수정 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/room-observing-sessions/{sessionId}/video
   * STARTED 상태에서만 호출 가능.
   */
  async recordRoomObservingVideo(
    visitCounselId: string,
    sessionId: string,
    request: RecordRoomObservingVideoRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/room-observing-sessions/${sessionId}/video`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '방 옵저빙 영상 수정에 실패했습니다.');
    }
  },

  /**
   * 방 옵저빙 세션 완료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/room-observing-sessions/{sessionId}/complete
   * STARTED 상태에서만 호출 가능. 영상·이사물품 기록 후 세션을 COMPLETED로 전환.
   */
  async completeRoomObservingSession(
    visitCounselId: string,
    sessionId: string
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/room-observing-sessions/${sessionId}/complete`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(
        error,
        '방 옵저빙 세션 완료에 실패했습니다.',
        '방 옵저빙 세션 완료에 실패했습니다.'
      );
    }
  },

  /**
   * 방 옵저빙 세션 이사 물품 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/room-observing-sessions/{sessionId}/moving-items
   * STARTED 상태에서 호출.
   */
  async recordRoomObservingMovingItems(
    visitCounselId: string,
    sessionId: string,
    request: RecordRoomObservingMovingItemsRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/room-observing-sessions/${sessionId}/moving-items`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '이사 물품 기록에 실패했습니다.');
    }
  },

  /**
   * 추가공간 옵저빙 세션 이사 물품 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/additional-space-observing-sessions/{sessionId}/moving-items
   */
  async recordAdditionalSpaceObservingMovingItems(
    visitCounselId: string,
    sessionId: string,
    request: RecordAdditionalSpaceObservingMovingItemsRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/additional-space-observing-sessions/${sessionId}/moving-items`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '이사 물품 기록에 실패했습니다.');
    }
  },

  /**
   * 추가공간 옵저빙 세션 시작 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/additional-space-observing-sessions/{sessionId}/start
   * REQUESTED 상태에서만 호출 가능.
   */
  async startAdditionalSpaceObservingSession(
    visitCounselId: string,
    sessionId: string,
    request: StartAdditionalSpaceObservingSessionRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/additional-space-observing-sessions/${sessionId}/start`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '추가공간 옵저빙 세션 시작에 실패했습니다.');
    }
  },

  /**
   * 이사 유형 스펙 카탈로그 (PLANNER 전용)
   * GET /api/v1/resources/moving-type-specs
   */
  async getMovingTypeSpecs(): Promise<MovingTypeSpecificationResponse[]> {
    try {
      const response = await externalApiClient.get<{ items: MovingTypeSpecificationResponse[] }>(
        '/api/v1/resources/moving-type-specs',
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data.items ?? [];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '이사 유형 스펙 조회에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 이사 유형 인테이크 저장 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/moving-type
   * Intake 세션이 STARTED인 뒤에만 성공.
   */
  async recordMovingTypeIntake(
    visitCounselId: string,
    request: RecordMovingTypeIntakeRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/moving-type`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '이사 유형 저장에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 출발지 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/departure
   * 평수, 주소, 엘리베이터 유무, 운반방식 기록.
   */
  async recordDepartureSite(
    visitCounselId: string,
    request: RecordDepartureSiteRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/departure`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '출발지 기록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 도착지 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/arrival
   * 평수, 주소, 엘리베이터 유무, 운반방식 기록.
   */
  async recordArrivalSite(
    visitCounselId: string,
    request: RecordArrivalSiteRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/arrival`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '도착지 기록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 이사일 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/moving-date
   */
  async recordMovingDate(
    visitCounselId: string,
    request: RecordMovingDateRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/moving-date`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '이사일 기록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 포장방식 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/packing
   */
  async recordPacking(
    visitCounselId: string,
    request: RecordPackingRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/packing`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '포장방식 기록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * Room 짐 서비스 세트 일괄 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/moving-item-service-sets
   */
  async recordMovingItemServiceSets(
    visitCounselId: string,
    request: RecordMovingItemServiceSetsRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/moving-item-service-sets`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '짐 서비스 설정 저장에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 고객요청사항 기록 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/intake-session/customer-requests
   */
  async recordCustomerRequests(
    visitCounselId: string,
    request: RecordCustomerRequestsRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/customer-requests`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '고객요청사항 기록에 실패했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * Intake 세션 시작 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/intake-session/start
   * record API 호출 전 반드시 start 필요.
   */
  async startIntakeSession(visitCounselId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/start`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '상담 진행을 시작하지 못했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 세션 리뷰 완료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/session-review
   * 스케치·옵저빙·인테이크가 모두 완료된 뒤 호출. reviewedAt 기록.
   */
  async markSessionReviewed(visitCounselId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/session-review`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '세션 리뷰 완료 처리에 실패했습니다.');
    }
  },

  /**
   * 방문 상담 종료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/counsel/complete
   * session-review 완료 후 호출. completedAt 기록. 이후 변경 API 불가.
   */
  async completeVisitCounsel(visitCounselId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/counsel/complete`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '상담 종료 처리에 실패했습니다.');
    }
  },

  /**
   * Intake 세션 완료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/intake-session/complete
   * 완료 후에도 record 수정 가능 (PUT으로 재호출).
   */
  async completeIntakeSession(visitCounselId: string): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/intake-session/complete`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message ||
          '상담 진행을 완료하지 못했습니다.';
        throw new Error(msg);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 추가공간 옵저빙 세션 완료 (PLANNER 전용)
   * POST /api/v1/visit-counsels/{visitCounselId}/additional-space-observing-sessions/{sessionId}/complete
   * STARTED 상태에서만 호출 가능. 영상 업로드 후 세션을 COMPLETED로 전환.
   */
  async completeAdditionalSpaceObservingSession(
    visitCounselId: string,
    sessionId: string
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.post<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/additional-space-observing-sessions/${sessionId}/complete`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(
        error,
        '추가공간 옵저빙 세션 완료에 실패했습니다.',
        '추가공간 옵저빙 세션 완료에 실패했습니다.'
      );
    }
  },

  /**
   * 추가공간 옵저빙 세션 영상 수정 (PLANNER 전용)
   * PUT /api/v1/visit-counsels/{visitCounselId}/additional-space-observing-sessions/{sessionId}/video
   * STARTED 상태에서만 호출 가능.
   */
  async recordAdditionalSpaceObservingVideo(
    visitCounselId: string,
    sessionId: string,
    request: RecordAdditionalSpaceObservingVideoRequest
  ): Promise<VisitCounselView> {
    try {
      const response = await externalApiClient.put<VisitCounselView>(
        `/api/v1/visit-counsels/${visitCounselId}/additional-space-observing-sessions/${sessionId}/video`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw toPlannerApiError(error, '추가공간 옵저빙 영상 수정에 실패했습니다.');
    }
  },
};
